/** Deep step definitions for user-role page tours. */

function c(targetId, title, body, extra = {}) {
  return {
    targetId,
    title,
    body,
    size: "compact",
    elementType: extra.elementType || "button",
    placement: extra.placement || "bottom",
    action: extra.action,
    ...extra,
  };
}

function d(targetId, title, body, action, outcome, extra = {}) {
  return {
    targetId,
    title,
    body,
    action,
    outcome,
    size: "standard",
    elementType: extra.elementType || "module",
    placement: extra.placement || "bottom",
    tip: extra.tip,
    warning: extra.warning,
    ...extra,
  };
}

export const USER_PAGE_TOURS_DEEP = [
  {
    id: "user-home",
    title: "Compliance Home",
    route: "/user",
    description: "Every part of your compliance home screen",
    steps: [
      d(
        "tour-user-welcome",
        "Page header",
        "Your personalised compliance home — greeting, organisation, and status badges.",
        "Scan the header each time you log in.",
        "You know where you stand before starting work.",
        { elementType: "module" },
      ),
      c(
        "tour-user-greeting",
        "Greeting & name",
        "Shows Good morning/afternoon/evening and your first name.",
        {
          elementType: "stat",
          action: "Confirms you are logged in as the correct user.",
        },
      ),
      c(
        "tour-user-org",
        "Organisation name",
        "The tenant/company you belong to — all data is scoped here.",
        { elementType: "stat", action: "Verify this matches your employer." },
      ),
      c(
        "tour-user-compliance-label",
        "Readiness label",
        "Text such as Building readiness or Compliant — derived from assessment progress.",
        { elementType: "stat", placement: "bottom" },
      ),
      c(
        "tour-user-deadline",
        "DPDP deadline",
        "Days remaining until 13 May 2027 compliance deadline.",
        { elementType: "stat", action: "Prioritise modules before this date." },
      ),
      c(
        "tour-user-road-progress",
        "Road progress",
        "Phases completed vs total (5) and overall % score.",
        {
          elementType: "stat",
          action: "Track how far along the roadmap you are.",
        },
      ),
      c(
        "tour-user-refresh",
        "Refresh button",
        "Reloads lifecycle data from the server.",
        {
          elementType: "button",
          action: "Click after submitting an assessment to update progress.",
        },
      ),
      c(
        "tour-user-dashboard-btn",
        "Open Dashboard",
        "Shortcut to the Integration Dashboard metrics page.",
        {
          elementType: "button",
          action: "Click to view consent, DSAR, and form stats.",
        },
      ),
      d(
        "tour-user-roadmap",
        "Compliance roadmap",
        "Five phases: Gap → PIA → ROPA → TPRM → Breach. Colour shows done vs pending.",
        "Click a phase to jump to that module.",
        "You always know the next priority.",
        { elementType: "card", placement: "top" },
      ),
      d(
        "tour-user-recommendations",
        "Smart recommendations",
        "Suggested next actions based on your incomplete modules and policies.",
        "Follow the top recommendation when unsure what to do.",
        "Guided daily workflow without guessing.",
        { elementType: "panel", placement: "top" },
      ),
    ],
  },
  {
    id: "user-dashboard",
    title: "Integration Dashboard",
    route: "/user/dashboard",
    description: "Every metric card, integration tile, and shortcut",
    steps: [
      d(
        "tour-dash-header",
        "Dashboard title",
        "Privacy integration hub — consent, cookies, DSAR, DSPM, and forms.",
        "Use Refresh for live numbers.",
        "Leadership-ready metrics.",
        { elementType: "module" },
      ),
      c(
        "tour-dash-refresh",
        "Refresh data",
        "Pulls latest stats from Consent, DSAR, and form APIs.",
        { elementType: "button", action: "Click before meetings or exports." },
      ),
      c(
        "tour-dash-breach-live",
        "Live breach status",
        "Compact live breach count in the header — updates automatically without Refresh.",
        {
          elementType: "button",
          action: "Click to open Breach Management when counts rise.",
        },
      ),
      c(
        "tour-dash-last-refreshed",
        "Last refreshed time",
        "Shows when data was last loaded.",
        { elementType: "stat", action: "Check if numbers are stale." },
      ),
      d(
        "tour-dash-stats",
        "Top summary row",
        "Four KPI tiles: compliance score, forms done, integrations active, etc.",
        "Read left-to-right for a 10-second health check.",
        "Quick org-wide snapshot.",
        { elementType: "stat" },
      ),
      d(
        "tour-dash-consent",
        "User consent donut",
        "Accepted vs revoked vs declined consent events from your CMP.",
        "Click card to open detailed consent modal.",
        "Spot consent drop-offs.",
        { elementType: "card", placement: "right" },
      ),
      d(
        "tour-dash-cookie",
        "Cookie consent donut",
        "Cookie banner accept/reject/pending counts.",
        "Open detail view for cookie compliance gaps.",
        "Cookie programme visibility.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-dash-dsar",
        "DSAR requests",
        "Processed vs pending Data Subject Access Requests.",
        "Act when pending > 0.",
        "Avoid DSAR SLA breaches.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-dash-ropa",
        "ROPA progress card",
        "Sections filled, progress %, and submission count.",
        "Click View More for ROPA breakdown.",
        "Processing register status.",
        { elementType: "card", placement: "right" },
      ),
      d(
        "tour-dash-gap",
        "Gap assessment card",
        "Sections completed, compliance score, and gaps count.",
        "Open when score is low.",
        "Baseline assessment health.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-dash-dspm",
        "DSPM / data inventory",
        "Sensitive records discovered by DSPM scanning.",
        "Review unclassified data types.",
        "Data discovery coverage.",
        { elementType: "card", placement: "right" },
      ),
      d(
        "tour-dash-pia",
        "PIA card",
        "Sensitive fields, encryption status, and submission counts.",
        "Fix unencrypted sensitive fields.",
        "Impact assessment readiness.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-dash-tprm",
        "Third-party risk card",
        "Vendor totals, high-risk count, and average score.",
        "Prioritise vendors below 50%.",
        "Vendor accountability under DPDP.",
        { elementType: "card", placement: "right" },
      ),
      d(
        "tour-dash-privacy",
        "Privacy notices card",
        "Organisational disclosure and notice management (beta).",
        "Expand when module is enabled.",
        "Notice management overview.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-dash-quick-actions",
        "Quick actions panel",
        "One-click shortcuts to Gap, ROPA, PIA, TPRM, and Reports.",
        "Use instead of hunting through menus.",
        "Faster daily navigation.",
        { elementType: "panel", placement: "top" },
      ),
      c(
        "tour-dash-qa-gap",
        "Quick: Gap Assessment",
        "Jump straight to the gap questionnaire.",
        {
          elementType: "button",
          action: "Start or continue baseline assessment.",
        },
      ),
      c(
        "tour-dash-qa-ropa",
        "Quick: ROPA",
        "Open Records of Processing Activities.",
        { elementType: "button" },
      ),
      c("tour-dash-qa-pia", "Quick: PIA", "Open Privacy Impact Assessment.", {
        elementType: "button",
      }),
      c("tour-dash-qa-tprm", "Quick: TPRM", "Open vendor risk assessment.", {
        elementType: "button",
      }),
      c("tour-dash-qa-reports", "Quick: Reports", "Generate compliance PDF.", {
        elementType: "button",
      }),
    ],
  },
  {
    id: "user-gap",
    title: "Gap Assessment",
    route: "/user/gap-assessment",
    description:
      "Department selection, 10 DPDP sections, policy tips, and submission",
    steps: [
      d(
        "tour-gap-header",
        "Gap Assessment workspace",
        "Your DPDP Act 2023 baseline assessment. The header shows the active assessment cycle name (set by your Tenant Admin), auto-save status, View Table for past submissions, and Export for a PDF snapshot.",
        "Check the cycle name here — you can only submit while the cycle is open.",
        "You know whether this cycle is open, closed, or already submitted.",
        { elementType: "module" },
      ),
      d(
        "tour-gap-department",
        "Select your department first",
        "Every department keeps its own answers, attachments, and policy recommendations. Pick yours before the form loads — the yellow banner reminds you if nothing is selected.",
        "Choose the department you belong to from the dropdown.",
        "Your draft loads separately from other teams.",
        {
          elementType: "field",
          action:
            "Required before the form loads. During a live tour, your first department is previewed automatically so later steps can highlight the sidebar and questions.",
          warning:
            "Pick your own department here if the preview is not the one you use.",
        },
      ),
      c(
        "tour-gap-view-table",
        "View submissions table",
        "Opens every Gap Assessment submission for your organisation — filter by department, status, and export records.",
        {
          elementType: "button",
          action:
            "Use this to verify a past submission or compare scores across departments.",
        },
      ),
      d(
        "tour-gap-sidebar",
        "Left sidebar — your guide",
        "Stays visible while you scroll. Shows overall completion, live Yes/No/Partial counts for the current section, policy suggestions, and all 10 DPDP domains as quick-jump tabs.",
        "Glance here whenever you need orientation.",
        "You never lose track of progress across a long assessment.",
        { elementType: "panel", placement: "right", forceDock: true },
      ),
      c(
        "tour-gap-progress",
        "Section completion stats",
        "Live percentage for the section you are on, plus a breakdown of Yes, No, Partial, and unanswered questions.",
        {
          elementType: "stat",
          placement: "right",
          action: "Aim for 100% in each section before moving on.",
        },
      ),
      d(
        "tour-gap-policy-panel",
        "Policy assistant",
        "When you answer No or Partial, a circular Policy Agent appears at the bottom-right with your recommendation count.",
        "Drag the circle anywhere or tap it to preview and generate matched policies.",
        "Turns assessment gaps into actionable policy drafts without cluttering the page.",
        { elementType: "card", placement: "left", optional: true },
      ),
      d(
        "tour-gap-sections",
        "Gap Assessment sections",
        "Consent Management, Notice & Purpose Limitation, Data Principal Rights, Data Fiduciary Obligations, Breach Management, Cross-Border Data Transfers, and Governance & Record-Keeping.",
        "Work top-to-bottom or jump ahead — a checkmark shows completed sections.",
        "Structured coverage of every DPDP domain.",
        { elementType: "tab", placement: "right", forceDock: true },
      ),
      d(
        "tour-gap-section-banner",
        "Current section context",
        "Shows the section title, DPDP code, your department badge, question count, section number, and a mini progress bar for this section only.",
        "Read the description — it explains what auditors expect in this domain.",
        "Clear scope before you start answering.",
        { elementType: "card", placement: "bottom", forceDock: true },
      ),
      d(
        "tour-gap-answer-area",
        "Answer each question",
        "Every card shows the question ID, DPDP reference, and risk severity. Choose Yes, No, or Partial (click again to clear). Add comments, assign an owner, and upload evidence attachments where required.",
        "Partial or No answers are fine — explain context in the comment box.",
        "Audit-ready answers with ownership and proof.",
        { elementType: "field", placement: "bottom", forceDock: true },
      ),
      d(
        "tour-gap-nav-footer",
        "Navigate between sections",
        "Previous moves back, the dot stepper jumps to any section, and Next advances once required questions in this section are answered. Answers auto-save — there is no separate Save Draft button.",
        "Use Next to validate the section; missing answers are highlighted.",
        "Safe to leave and return — progress is preserved automatically.",
        { elementType: "button", placement: "top" },
      ),
      d(
        "tour-gap-submit-btn",
        "Submit Assessment",
        "On the final section (Breach Management), Next becomes Submit Assessment. This locks your answers for the cycle so your Tenant Admin can review and score them.",
        "Complete all 10 sections first — incomplete sections block submission.",
        "A finalised baseline your organisation can build its roadmap on.",
        {
          elementType: "button",
          placement: "top",
          goToLastSection: true,
          warning:
            "Ask your Tenant Admin to open a cycle if Submit is disabled.",
        },
      ),
    ],
  },
  {
    id: "user-ropa",
    title: "ROPA",
    route: "/user/ropa",
    description:
      "Cover page (what / why / how, Manual vs Automated) then the 12-section assessment",
    steps: [
      d(
        "tour-ropa-cover-header",
        "ROPA landing page",
        "This hub is where you choose how to document processing — Manual guided form or Automated DSPM sync — under DPDP Act 2023 Section 8. The assessment form is a separate screen.",
        "Read the title and Section 8 badge. Next walks each block on this page.",
        "You know this is the cover page, not the 12-section form.",
        {
          elementType: "module",
          placement: "bottom",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      d(
        "tour-ropa-cover-what",
        "01 — What is ROPA?",
        "The master register of every personal data processing activity: purpose, principals, data categories, legal basis, flows, retention, and safeguards.",
        "This left-hand card is the definition. The form itself opens later via Start Manual ROPA.",
        "You can explain ROPA as a factual inventory, not a risk score.",
        {
          elementType: "card",
          placement: "right",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      d(
        "tour-ropa-cover-why",
        "02 — Why is it required?",
        "Section 8 DPDP Act 2023 — you must prove lawful processing. Auditors and the Board will ask for this register.",
        "This is the middle card on the left. It is why the register is mandatory.",
        "You understand this is required evidence, not optional paperwork.",
        {
          elementType: "card",
          placement: "right",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      d(
        "tour-ropa-cover-how",
        "03 — How is it used?",
        "A completed ROPA feeds PIAs, breach timelines, DSAR, TPRM, and the compliance roadmap. Revisit it when processing changes.",
        "This is the last card on the left. It is how the register is used after you fill it.",
        "You know this register powers the rest of the privacy programme.",
        {
          elementType: "card",
          placement: "right",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      d(
        "tour-ropa-cover-manual",
        "Manual ROPA card",
        "Recommended path: a guided 12-section form (~20–25 min) for organisation, legal basis, data flows, retention, and security.",
        "This is the top card on the right. Next explains its two buttons.",
        "You know Manual ROPA is the recommended way to document processing.",
        {
          elementType: "card",
          placement: "left",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      c(
        "tour-ropa-cover-start",
        "Start Manual ROPA",
        "This button opens the 12-section assessment on a new page. The tour will take you there after the cover page.",
        {
          elementType: "button",
          placement: "left",
          route: "/user/ropa",
          action: "Do not click it yet — use tour Next. The next cover cards finish first.",
        },
      ),
      c(
        "tour-ropa-view-submissions",
        "View Submissions",
        "Opens submitted ROPA records for your organisation. Use this if a register may already exist.",
        {
          elementType: "button",
          placement: "left",
          route: "/user/ropa",
          action: "Stays on the cover flow — it does not start a new assessment.",
        },
      ),
      d(
        "tour-ropa-cover-automated",
        "Automated ROPA card",
        "DSPM-synced registers (for example DataForesight). No 12-section form — records are reviewed and kept in sync.",
        "This is the lower card on the right. Use it only when DSPM is connected.",
        "You know Automated ROPA is the auto-synced alternative.",
        {
          elementType: "card",
          placement: "left",
          size: "compact",
          route: "/user/ropa",
        },
      ),
      c(
        "tour-ropa-cover-automated-btn",
        "View Automated ROPA",
        "Opens auto-synced records. Next leaves this cover page and opens the Manual assessment form.",
        {
          elementType: "button",
          placement: "left",
          route: "/user/ropa",
          action: "Requires a DSPM connection. Tour Next now opens the assessment.",
        },
      ),
      d(
        "tour-ropa-header",
        "Assessment workspace",
        "You are now on the Manual ROPA form (not the cover). Header shows title, auto-save, View Table, and Submit.",
        "Confirm the URL is the assessment, and the 12-section sidebar is on the left.",
        "You are filling the processing register for one department.",
        { elementType: "module", route: "/user/ropa/manual", size: "compact" },
      ),
      d(
        "tour-ropa-department",
        "Department selector",
        "ROPA drafts and submissions are scoped per department. Select yours before the 12-section wizard appears. A Locked badge means this department already submitted.",
        "Pick the department whose processing activities you are documenting.",
        "Separate drafts per team — no cross-contamination.",
        {
          elementType: "field",
          route: "/user/ropa/manual",
          action: "Required before the form loads.",
          warning:
            "During a live tour, the first department is previewed automatically so form steps can highlight.",
        },
      ),
      c(
        "tour-ropa-stats",
        "Progress counters",
        "Filled shows how many sections have all required fields complete. Section shows your current step (e.g. 2/12). Saving… appears while auto-save runs.",
        { elementType: "stat", route: "/user/ropa/manual" },
      ),
      c(
        "tour-ropa-view-table",
        "View submissions table",
        "From the assessment header — the same submissions list as on the cover page, so you can check existing records without leaving the form.",
        {
          elementType: "button",
          route: "/user/ropa/manual",
          action: "Use this to verify a past submission while you work.",
        },
      ),
      d(
        "tour-ropa-sidebar",
        "12-section sidebar",
        "Completion gauge (Started → In Progress → On Track) plus every ROPA section: Organisation Details, Processing Activity, Legal Basis, Data Principals, Personal Data, Processing Lifecycle, Data Sharing, Retention & Erasure, Security Safeguards, Rights Handling, Incident Management, and Approval & Sign-off.",
        "Click any section to jump — green ticks mark completed ones.",
        "Full visibility of the 12-part register structure.",
        {
          elementType: "panel",
          placement: "right",
          forceDock: true,
          route: "/user/ropa/manual",
        },
      ),
      d(
        "tour-ropa-section-banner",
        "Section header card",
        "Current section title, step number, completion badge, and field count. Shows View Only when submitted.",
        "Read the section purpose before filling fields.",
        "Context for the register slice you are documenting.",
        {
          elementType: "card",
          placement: "bottom",
          forceDock: true,
          route: "/user/ropa/manual",
        },
      ),
      d(
        "tour-ropa-form",
        "Section fields",
        "Guided fields for the active section — organisation details, processing purposes, legal basis, data categories, retention periods, transfers, security measures, and sign-off. Required fields show a red asterisk. Tenant admins may add custom fields.",
        "Fill required fields before clicking Next — validation runs per section.",
        "Complete Section 8-style register data for your department.",
        {
          elementType: "field",
          placement: "bottom",
          forceDock: true,
          route: "/user/ropa/manual",
        },
      ),
      d(
        "tour-ropa-nav-footer",
        "Section navigation",
        "Previous, dot stepper (12 dots), and Next Section. Validates required fields before advancing. Draft auto-saves as you type.",
        "Fix validation errors shown in red before moving forward.",
        "Steady progress through all 12 sections.",
        { elementType: "button", placement: "top", route: "/user/ropa/manual" },
      ),
      c(
        "tour-ropa-submit-btn",
        "Submit ROPA",
        "In the header — finalises the register for this department once every section is complete.",
        {
          elementType: "button",
          route: "/user/ropa/manual",
          action:
            "Submit only when all 12 sections show as filled. Your admin can reopen if corrections are needed.",
          goToLastSection: true,
        },
      ),
    ],
  },
  {
    id: "user-pia",
    title: "PIA / DPIA",
    route: "/user/pia",
    description:
      "6-step privacy impact assessment — legal basis, risk score, and sign-off",
    steps: [
      d(
        "tour-pia-header",
        "Privacy Impact Assessment",
        "DPIA-style review for higher-risk processing — sensitive data, large-scale profiling, or new technology. Header shows auto-save status and navigation actions.",
        "Start a PIA when a project touches personal data in a new or risky way.",
        "Documented risk review your DPO can sign off on.",
        { elementType: "module" },
      ),
      d(
        "tour-pia-department",
        "Department scope",
        "Each department maintains its own PIA draft. Select yours before the 6-step wizard loads.",
        "Choose the department owning this processing activity.",
        "Isolated drafts per team.",
        {
          elementType: "field",
          action: "Required — form hidden until selected.",
          warning: "During a live tour, the first department is previewed automatically so form steps can highlight.",
        },
      ),
      c(
        "tour-pia-progress",
        "Step progress",
        "Done counts completed steps. Step shows current position (e.g. 2/6). Saving… indicates auto-save.",
        { elementType: "stat" },
      ),
      c(
        "tour-pia-view-table",
        "View PIA submissions",
        "All submitted PIAs across departments — status, scores, and detail drill-down.",
        { elementType: "button" },
      ),
      d(
        "tour-pia-sidebar",
        "6 assessment steps",
        "General Info, Data Details, Data Flow, Risk Assessment, Vendor & Controls, and Safeguards & Review. Each shows completion status — click to jump.",
        "Work in order for best results; sidebar tracks what's done.",
        "Full PIA lifecycle in six structured steps.",
        { elementType: "panel", placement: "right", forceDock: true },
      ),
      d(
        "tour-pia-section-banner",
        "Current step banner",
        "Step title, icon, question/field count, and section progress bar. View Only badge when submitted.",
        "Note which step you are on before answering.",
        "Clear step context at all times.",
        { elementType: "card", placement: "bottom", forceDock: true },
      ),
      d(
        "tour-pia-form",
        "Step fields & risk tools",
        "Varies by step: project ID and legal basis (multi-select cards), data categories and flows, live risk score from likelihood × impact, vendor controls, transfer safeguards, and evidence attachments. Red validation banner lists missing required fields.",
        "In General Info, pick all applicable legal bases. In Risk Assessment, set likelihood and impact — score updates live.",
        "Specific, defensible DPIA content — not vague 'low risk' answers.",
        { elementType: "field", placement: "bottom", forceDock: true },
      ),
      d(
        "tour-pia-nav-footer",
        "Previous / Next navigation",
        "Previous goes back, dots jump between the 6 steps, and the header button saves progress and advances. On the last step it becomes Submit PIA.",
        "Fix validation errors before continuing — required fields block Next.",
        "Progress preserved automatically between steps.",
        { elementType: "button", placement: "top" },
      ),
      c(
        "tour-pia-submit",
        "Save & Continue / Submit",
        "Header action — advances to the next step or submits on the final Safeguards & Review step.",
        {
          elementType: "button",
          placement: "top",
          goToLastSection: true,
          tip: "Label changes to Submit on the last step.",
        },
      ),
    ],
  },
  {
    id: "user-tprm",
    title: "TPRM",
    route: "/user/tprm",
    description:
      "Vendor risk questionnaire — score panel, 7 sections, attachments",
    steps: [
      d(
        "tour-tprm-header",
        "Third-Party Risk Management",
        "Assess vendors who handle personal data on your behalf — payroll, cloud, marketing tools, processors. DPDP accountability extends to your supply chain.",
        "Complete one assessment per vendor relationship per department.",
        "Scored vendor record before sharing personal data.",
        { elementType: "module" },
      ),
      d(
        "tour-tprm-department",
        "Owning department",
        "Links this vendor assessment to the department that uses or manages the vendor.",
        "Select the department responsible for this vendor.",
        "Assessments grouped correctly for your org.",
        {
          elementType: "field",
          action: "Required for tenant users before the questionnaire loads.",
          warning: "During a live tour, the first department is previewed automatically so form steps can highlight.",
        },
      ),
      c(
        "tour-tprm-view-table",
        "View vendor submissions",
        "All TPRM assessments — risk scores, departments, and submission history.",
        { elementType: "button" },
      ),
      c(
        "tour-tprm-draft",
        "Progress & auto-save",
        "Overall Progress bar (answered/total and %), plus Draft saved timestamp. Answers persist automatically — no manual save needed.",
        {
          elementType: "stat",
          action: "Watch for Saving draft… while typing.",
        },
      ),
      d(
        "tour-tprm-sidebar",
        "Score + section navigator",
        "Left panel combines your live risk score with the full section list.",
        "Monitor score as you answer — low scores warrant admin follow-up.",
        "Real-time risk visibility while you work.",
        { elementType: "panel", placement: "right" },
      ),
      c(
        "tour-tprm-score",
        "Overall risk score",
        "Arc gauge with risk band (Low / Medium / High / Critical), answered vs remaining counts, and completion percentage.",
        {
          elementType: "stat",
          placement: "right",
          action: "Score recalculates as you complete required questions.",
        },
      ),
      d(
        "tour-tprm-sections",
        "7 questionnaire sections",
        "General Information, Cybersecurity & Data, Compliance & Regulatory, Financial Stability, Operational Risk, Legal, and Reputation. Each row shows section score and completion.",
        "Click a section to jump — complete required questions in order.",
        "Structured vendor due diligence across all risk domains.",
        { elementType: "tab", placement: "right" },
      ),
      d(
        "tour-tprm-section-banner",
        "Active section header",
        "Section title, question count, step indicator, and per-section progress bar.",
        "Read before answering — scopes the vendor topic.",
        "Know exactly which risk domain you are assessing.",
        { elementType: "card", placement: "left" },
      ),
      d(
        "tour-tprm-questions",
        "Vendor questions",
        "Numbered question cards with Yes/No, select, or text inputs depending on question type. Upload SOC 2 reports, DPAs, or security whitepapers as attachments where allowed.",
        "Answer every required question — validation lists what's missing.",
        "Evidence-backed vendor risk profile.",
        { elementType: "field", placement: "bottom", forceDock: true },
      ),
      d(
        "tour-tprm-nav-footer",
        "Section navigation",
        "Previous, dot stepper across sections, and Next Section (validates required answers). On the last section, Next becomes Submit Assessment.",
        "Upload attachments before leaving a section if evidence is required.",
        "Smooth progression through the full questionnaire.",
        { elementType: "button", placement: "top" },
      ),
      c(
        "tour-tprm-submit",
        "Submit vendor assessment",
        "Appears on the final section — locks answers and records the final risk score.",
        {
          elementType: "button",
          placement: "top",
          goToLastSection: true,
          tip: "Review the overall score in the sidebar before submitting.",
        },
      ),
    ],
  },
  {
    id: "user-breach",
    title: "Breach Management",
    route: "/user/breach-management",
    description: "Stats, report, checklist, and register",
    steps: [
      d(
        "tour-breach-stats",
        "Your at-a-glance breach health",
        "Four numbers worth knowing: how many incidents are active right now, how many are critical or high severity, how many notifications are still pending, and how fast you're typically detecting incidents.",
        "Check this daily while any incident is open — it's the fastest way to know if something needs your attention.",
        "You always know your organisation's real-time incident posture.",
        { elementType: "stat" },
      ),
      d(
        "tour-breach-report",
        "Report a breach the moment you spot one",
        "Opens the structured breach-reporting workspace, built around Section 9 of the DPDP Act.",
        "Don't wait to gather every detail first — start the report as soon as you're aware of an incident and add information as it comes in.",
        "A properly timestamped, structured record instead of a scramble of emails.",
        { elementType: "button" },
      ),
      d(
        "tour-breach-checklist",
        "Follow the response checklist",
        "Walks you through the notification steps that matter: DPO review, informing the board, and notifying affected users where required.",
        "Tick items off as you complete them — the checklist is your evidence trail if a regulator asks what you did and when.",
        "You stay inside the 72-hour notification window DPDP expects.",
        { elementType: "card", placement: "left" },
      ),
      c(
        "tour-breach-filter",
        "Filter by severity",
        "Narrows the register down to just Critical, High, Medium, or Low incidents.",
        {
          elementType: "tab",
          action: "Filter to Critical/High first when triaging your day.",
        },
      ),
      c(
        "tour-breach-export",
        "Export the full register",
        "Downloads your breach log as a file you can hand to auditors or leadership.",
        { elementType: "button" },
      ),
      d(
        "tour-breach-log",
        "The full incident history",
        "Every recorded breach with its severity, current status, and key dates in one table.",
        "Review this before any regulator call or board meeting — it's your single source of truth.",
        "A complete, defensible audit trail.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "user-consent",
    title: "Consent Variance",
    route: "/user/consent-variance",
    description: "Integrations, table, and reconsent",
    steps: [
      d(
        "tour-consent-header",
        "Why this page exists",
        "It cross-checks the consent you've actually recorded against the data DSPM discovers you're processing — catching cases where you're using data without matching consent on file.",
        "Come back here regularly, not just once — new variances show up as your systems and data change.",
        "Gaps between 'what we're allowed to do' and 'what we're actually doing' get caught early.",
        { elementType: "module" },
      ),
      d(
        "tour-consent-integrations",
        "Make sure your data feeds are live",
        "Two status badges — Consent and DSPM — show whether each system is actively feeding this page real data. Refresh pulls the latest.",
        "If either badge isn't green, treat the variance table below with caution until it is — the comparison is only as good as its data sources.",
        "Confidence that what you're seeing reflects reality, not stale data.",
        { elementType: "stat", placement: "left" },
      ),
      d(
        "tour-consent-table",
        "Where the mismatches show up",
        "Lists each data owner alongside where their consent record and their actual discovered data disagree.",
        "Open a row to investigate the specific mismatch, and trigger a reconsent flow when someone's data has outgrown what they agreed to.",
        "A consent programme that actually matches what's happening with your data.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "user-policy",
    title: "Policy Store",
    route: "/user/policy-store",
    description: "Upload, generated, and org policies",
    steps: [
      d(
        "tour-policy-header",
        "One home for every policy",
        "The single place your organisation keeps its DPDP policies — whether they were written by your team or generated from your Gap Assessment answers.",
        "Check the total document count here before assuming a policy doesn't exist — it may already be uploaded.",
        "No more hunting through email threads or shared drives for the latest policy version.",
        { elementType: "module" },
      ),
      c(
        "tour-policy-refresh",
        "Get the latest list",
        "Reloads the page with any policies uploaded or generated since you last checked.",
        { elementType: "button" },
      ),
      c(
        "tour-policy-upload",
        "Add your own policy",
        "Uploads an approved PDF or Word document into the shared store.",
        {
          elementType: "button",
          action:
            "Use this for policies your team has written and had approved outside the platform.",
        },
      ),
      d(
        "tour-policy-grid",
        "Browse and share policies",
        "Each card shows a policy's title and type, with options to preview or download it.",
        "Open a card to read the full policy, or download it to share with someone outside the platform.",
        "Everyone on your team can find and reference the right policy in seconds.",
        { elementType: "card", placement: "top" },
      ),
    ],
  },
  {
    id: "user-reports",
    title: "Compliance Reports",
    route: "/user/reports",
    description: "Scope, tabs, preview, and export",
    steps: [
      d(
        "tour-reports-header",
        "Turn your data into a report",
        "Generates polished PDF exports of your compliance status — Executive, Summary, or Detailed, depending on who's reading it.",
        "Hit Refresh first so the report reflects your latest assessment answers, not last week's.",
        "A ready-to-share document instead of screenshots stitched together.",
        { elementType: "module" },
      ),
      c(
        "tour-reports-refresh",
        "Pull in the latest numbers",
        "Refreshes the preview with your most recent assessment and module data.",
        { elementType: "button" },
      ),
      d(
        "tour-reports-panel",
        "Choose your scope and depth",
        "A data scope filter alongside three report types — Executive, Summary, and Detailed.",
        "Match the depth to your audience: keep it short for leadership, detailed for auditors.",
        "The right report for the right reader, every time.",
        { elementType: "tab", placement: "left" },
      ),
      c(
        "tour-reports-tab-exec",
        "Executive tab",
        "A one-glance summary built for leadership and board packs — headline numbers only.",
        {
          elementType: "tab",
          action:
            "Pick this when you need something a busy exec can read in under a minute.",
        },
      ),
      c(
        "tour-reports-tab-summary",
        "Summary tab",
        "A mid-level view — enough detail to understand where things stand without the full breakdown.",
        { elementType: "tab" },
      ),
      c(
        "tour-reports-tab-detail",
        "Detailed tab",
        "The full picture: every module's breakdown, with supporting charts.",
        {
          elementType: "tab",
          action: "Use this for audits or deep internal reviews.",
        },
      ),
      d(
        "tour-reports-preview",
        "Check it before you send it",
        "Shows exactly what will appear in the exported PDF — charts, tables, and all.",
        "Scroll through and sanity-check the numbers before exporting; it's much easier to fix here than after it's already shared.",
        "Confidence that what goes out the door is accurate.",
        { elementType: "panel", placement: "top" },
      ),
      c(
        "tour-reports-export",
        "Export the PDF",
        "Downloads the current preview exactly as shown, ready to share or archive.",
        {
          elementType: "button",
          action:
            "Export only after you've confirmed the scope, type, and preview all look right.",
        },
      ),
    ],
  },
];
